import moment, { Moment, unitOfTime } from 'moment/moment'

type IntervalType = 'hours' | 'days' | 'weeks' | 'months'
type DateInput = Date | string | Moment
type FormattedOutput<T> = T extends string ? string : Date

interface IntervalConfig {
  add: (m: Moment) => Moment
  endOf: (m: Moment) => Moment
  threshold: unitOfTime.StartOf
}

export interface DateInterval<T = string> {
  startDate: FormattedOutput<T>
  endDate: FormattedOutput<T>
}

const intervalConfig: Record<IntervalType, IntervalConfig> = {
  hours: {
    add: m => m.add(1, 'hour'),
    endOf: m => m.clone().endOf('hour'),
    threshold: 'hour',
  },
  days: {
    add: m => m.add(1, 'day'),
    endOf: m => m.clone().endOf('day'),
    threshold: 'day',
  },
  weeks: {
    add: m => m.add(1, 'week').startOf('week'),
    endOf: m => m.clone().endOf('week'),
    threshold: 'week',
  },
  months: {
    add: m => m.add(1, 'month').startOf('month'),
    endOf: m => m.clone().endOf('month'),
    threshold: 'month',
  },
  // years: {
  //   add: (m) => m.add(1, 'year').startOf('year'),
  //   endOf: (m) => m.clone().endOf('year'),
  //   threshold: 'year'
  // }
}

export const splitIntervalByPeriod = <T extends string>(
  startDate: DateInput,
  endDate: DateInput,
  intervalType: IntervalType,
  format?: T
): DateInterval<T>[] => {
  const currentStart = moment(startDate)
  const end = moment(endDate)

  if (currentStart.isAfter(end)) {
    throw new Error('startDate must be before or equal to endDate')
  }

  const config = intervalConfig[intervalType]
  if (!config) {
    throw new Error('Unsupported interval type. Use: hours, days, weeks, months, years')
  }

  const intervals: DateInterval<T>[] = []
  let currentIntervalStart = currentStart.clone()

  while (currentIntervalStart.isSameOrBefore(end, config.threshold)) {
    const currentEnd = config.endOf(currentIntervalStart)

    intervals.push({
      startDate: (format
        ? currentIntervalStart.format(format)
        : currentIntervalStart.toISOString()) as FormattedOutput<T>,
      endDate: (format
        ? currentEnd.format(format)
        : currentEnd.toISOString()) as FormattedOutput<T>,
    })

    currentIntervalStart = config.add(currentIntervalStart)
  }

  return intervals
}
